package org.zest.insan_360_website.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.rememberCoroutineScope
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.forms.Input
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.common.PlaceholderColor
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.onSubmit
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Form
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Text
import org.zest.insan_360_website.components.layouts.*
import org.zest.insan_360_website.components.widgets.DotLoading
import org.zest.insan_360_website.components.widgets.LocalToast
import org.zest.insan_360_website.data.LanguageSettings
import org.zest.insan_360_website.data.localized
import org.zest.insan_360_website.data.page_states.ResetPasswordPageState
import org.zest.insan_360_website.model.ResetPasswordRequest
import org.zest.insan_360_website.utils.NetworkUtils
import org.zest.insan_360_website.utils.processResponse

@Page("/reset-password")
@Layout(".components.layouts.MainPageLayout")
@Composable
fun ResetPasswordPage() {
    val ctx = rememberPageContext()
    val token = ctx.route.params["token"] ?: ""
    
    val state = ResetPasswordPageState.state
    
    val toast = LocalToast.current
    val scope = rememberCoroutineScope()
    val authWordLibrary = LanguageSettings.AuthenticationPage

    val submitForm: () -> Unit = {
        if (token.isBlank()) {
            toast.show(authWordLibrary.invalidToken.localized)
        } else if (!state.newPassword.matches(Regex("^(?=.*[A-Z])(?=.*\\d).{8,}$"))) {
            toast.show(authWordLibrary.passwordComplexError.localized)
        } else if (state.newPassword != state.confirmPassword) {
            toast.show(authWordLibrary.passwordConfirmation.localized)
        } else {
            ResetPasswordPageState.state = state.copy(isProcessing = true)
            scope.launch {
                try {
                    val response = NetworkUtils.postBasicApi(
                        "/api/auth/reset-password", 
                        ResetPasswordRequest(token, state.newPassword)
                    )
                    processResponse<Unit>(
                        response = response,
                        toast = toast,
                        fallbackErrorLog = "Failed to reset password"
                    ) {
                        ctx.router.navigateTo("/")
                    }
                } catch (_: Exception) {
                    toast.show(LanguageSettings.General.networkFailure.localized)
                } finally {
                    ResetPasswordPageState.state = state.copy(isProcessing = false)
                }
            }
        }
    }

    StandaloneFormContainer {
        Column(
            Modifier
                .backgroundColor(Colors.White)
                .fillMaxSize()
                .padding(2.cssRem)
                .gap(1.cssRem),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center
        ) {
            H2(
                Modifier
                    .textAlign(TextAlign.Center)
                    .margin(0.px)
                    .toAttrs()
            ) { 
                Text(authWordLibrary.resetPassword.localized) 
            }

            if (token.isBlank()) {
                SpanText(
                    authWordLibrary.invalidLink.localized,
                    Modifier.textAlign(TextAlign.Center).fontSize(1.cssRem).margin(top = 1.cssRem)
                )
                A(
                    attrs = Modifier
                        .fontSize(0.75.cssRem)
                        .color(darkGreenColor)
                        .margin(top = 1.cssRem)
                        .toAttrs(),
                    href = "/"
                ) {
                    Text(authWordLibrary.backToLogin.localized)
                }
            } else {
                Form(
                attrs = Modifier.fillMaxWidth().toAttrs {
                    onSubmit {
                        it.preventDefault()
                        submitForm()
                    }
                }
            ) {
                Column(Modifier.fillMaxWidth().gap(1.cssRem)) {
                    Input(
                        modifier = baseInputStyle.toModifier().attrsModifier {
                            attr("autocomplete", "new-password")
                            attr("name", "new-password")
                        },
                        focusBorderColor = lightGreenColor,
                        placeholderColor = PlaceholderColor(lightGreenColor),
                        type = InputType.Password,
                        value = state.newPassword,
                        onValueChange = { ResetPasswordPageState.state = state.copy(newPassword = it) },
                        placeholder = authWordLibrary.newPassword.localized
                    )
                    Input(
                        modifier = baseInputStyle.toModifier().attrsModifier {
                            attr("autocomplete", "new-password")
                            attr("name", "confirm-password")
                        },
                        focusBorderColor = lightGreenColor,
                        placeholderColor = PlaceholderColor(lightGreenColor),
                        type = InputType.Password,
                        value = state.confirmPassword,
                        onValueChange = { ResetPasswordPageState.state = state.copy(confirmPassword = it) },
                        placeholder = authWordLibrary.confirmPassword.localized
                    )
                    Button(
                        enabled = !state.isProcessing,
                        onClick = { submitForm() },
                        modifier = buttonColorStyle.toModifier()
                            .fillMaxWidth()
                    ) {
                        if (state.isProcessing) {
                            DotLoading(Colors.White)
                        } else {
                            SpanText(
                                authWordLibrary.resetPassword.localized,
                                contentButtonTextStyle.toModifier().fontSize(1.cssRem)
                            )
                        }
                    }
                }
            }
                A(
                    attrs = Modifier
                        .fontSize(0.75.cssRem)
                        .color(darkGreenColor)
                        .toAttrs(),
                    href = "/"
                ) {
                    Text(authWordLibrary.backToLogin.localized)
                }
            }
        }
    }
}
